'use client';

import { useRef, useEffect, useCallback, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { motion } from 'motion/react';
import { useTranslations, useLocale } from 'next-intl';
import { cn } from '@lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { ActivityIcon } from './dashboard-icons';
import { customRichTextTags } from '@/utils';

// ── Constants ──

const DASHBOARD_BLEED_STYLE = { marginLeft: 'calc(-50vw + 50%)' } as const;
const CHART_MARGIN = { top: 10, right: 30, left: 30, bottom: 5 } as const;
const YAXIS_CHART_MARGIN = { top: 10, right: 0, left: 0, bottom: 5 } as const;
const YAXIS_WIDTH = 45;
const XAXIS_HEIGHT = 40;
const ARROW_SCROLL_AMOUNT = 100;
const TOOLTIP_CURSOR_STYLE = {
  stroke: '#FF4242',
  strokeWidth: 1,
  strokeDasharray: '4 4',
} as const;
const DOT_STYLE = {
  r: 4,
  fill: '#FF4242',
  stroke: 'white',
  strokeWidth: 2,
} as const;
const YAXIS_STYLE = { width: YAXIS_WIDTH } as const;

const MOBILE_VISIBLE_MONTHS = 3;
const DESKTOP_VISIBLE_MONTHS = 7;

function generateChartLabels(
  lastYear: number,
  lastMonth: number,
  count: number,
  locale: string,
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, {
    year: '2-digit',
    month: 'short',
  });

  const labels: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    let month = lastMonth - i;
    let year = lastYear;
    while (month <= 0) {
      month += 12;
      year -= 1;
    }
    const date = new Date(year, month - 1);
    labels.push(formatter.format(date));
  }
  return labels;
}

// ── Types ──

type TickProps = {
  x: string | number;
  y: string | number;
  payload: { value: string };
};

type ChartDataItem = {
  name: string;
  value: number;
};

// ── Hooks ──

function useChartScroll(isDesktop: boolean, totalMonths: number) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = el.scrollWidth;
    }
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;

      const innerWidth = scrollEl.scrollWidth;
      const visibleWidth = scrollEl.clientWidth;
      const clickX =
        e.clientX - scrollEl.getBoundingClientRect().left + scrollEl.scrollLeft;

      const monthWidth = innerWidth / totalMonths;
      const clickedIndex = Math.floor(clickX / monthWidth);
      const clampedIndex = Math.max(0, Math.min(clickedIndex, totalMonths - 1));

      const targetCenter = (clampedIndex + 0.5) * monthWidth;
      const targetScroll = targetCenter - visibleWidth / 2;

      scrollEl.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth',
      });
    },
    [totalMonths],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        scrollRef.current?.scrollBy({
          left: -ARROW_SCROLL_AMOUNT,
          behavior: 'smooth',
        });
      }
      if (e.key === 'ArrowRight') {
        scrollRef.current?.scrollBy({
          left: ARROW_SCROLL_AMOUNT,
          behavior: 'smooth',
        });
      }
    },
    [],
  );

  return { scrollRef, handleClick, handleKeyDown };
}

// ── Sub-components ──

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: ReadonlyArray<Payload<number, string>>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="flex w-[71px] flex-col items-center rounded-[10px] border border-solid border-[#e2e8f0] bg-white p-[7px] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.06)] md:w-[100px] md:p-[10px]">
      <p className="text-[14px] font-bold leading-[24px] tracking-[-0.34px] text-3b-error-primary md:text-[20px]">
        {payload[0].value}
      </p>
      <p className="text-[10px] font-normal text-[#64748b] md:text-[14px]">
        {label}
      </p>
    </div>
  );
}

function FixedYAxisChart({
  chartData,
  isDesktop,
  yDomain,
}: {
  chartData: ChartDataItem[];
  isDesktop: boolean;
  yDomain: [number, number];
}) {
  return (
    <div
      className={cn('shrink-0', isDesktop ? 'h-64' : 'h-[215px]')}
      style={YAXIS_STYLE}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={YAXIS_CHART_MARGIN}>
          <YAxis
            domain={yDomain}
            width={YAXIS_WIDTH}
            axisLine={false}
            tickLine={false}
            tick={({ x, y, payload }: TickProps) => (
              <text
                x={x}
                y={y}
                textAnchor="end"
                fill="#64748b"
                fontSize={14}
                dy={4}
              >
                {payload.value}
              </text>
            )}
          />
          <XAxis
            dataKey="name"
            height={XAXIS_HEIGHT}
            tick={false}
            axisLine={false}
            tickLine={false}
          />
          <Area type="monotone" dataKey="value" stroke="none" fill="none" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ScrollableChart({
  chartData,
  isDesktop,
  scrollableStyle,
  scrollRef,
  chartLabel,
  onChartClick,
  onKeyDown,
  yDomain,
}: {
  chartData: ChartDataItem[];
  isDesktop: boolean;
  scrollableStyle: React.CSSProperties;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  chartLabel: string;
  onChartClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  yDomain: [number, number];
}) {
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={scrollRef}
      className="min-w-0 flex-1 cursor-pointer overflow-x-auto"
      role="region"
      aria-label={chartLabel}
      tabIndex={0}
      onClick={onChartClick}
      onKeyDown={onKeyDown}
    >
      <div
        className={cn(isDesktop ? 'h-64' : 'h-[215px]')}
        style={scrollableStyle}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF4242" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#FF4242" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="strokeGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#fda4af" />
                <stop offset="80%" stopColor="#fda4af" />
                <stop offset="100%" stopColor="#FF4242" />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e2e8f0"
            />
            <XAxis
              dataKey="name"
              height={XAXIS_HEIGHT}
              axisLine={false}
              tickLine={false}
              interval={0}
              dy={10}
              tick={({ x, y, payload }: TickProps) => (
                <text
                  x={x}
                  y={Number(y) + 22}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize={14}
                >
                  {payload.value}
                </text>
              )}
            />
            <YAxis domain={yDomain} hide />
            <Tooltip
              cursor={TOOLTIP_CURSOR_STYLE}
              content={<CustomTooltip />}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="url(#strokeGradient)"
              strokeWidth={3}
              dot={DOT_STYLE}
              fillOpacity={1}
              fill="url(#colorValue)"
              isAnimationActive={true}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Main Component ──

interface DashboardProps {
  chartData: number[];
  chartLastYear: number;
  chartLastMonth: number;
  chartYMax: number;
}

export default function Dashboard({
  chartData: chartValues,
  chartLastYear,
  chartLastMonth,
  chartYMax,
}: DashboardProps) {
  const t = useTranslations('Reanalysis.dashboard');
  const locale = useLocale();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const totalMonths = chartValues.length;
  const yDomain: [number, number] = [0, chartYMax];

  const thisMonthValue = chartValues[chartValues.length - 1] ?? 0;
  const { scrollRef, handleClick, handleKeyDown } = useChartScroll(
    isDesktop,
    totalMonths,
  );
  const visibleCount = isDesktop
    ? DESKTOP_VISIBLE_MONTHS
    : MOBILE_VISIBLE_MONTHS;

  const labels = useMemo(
    () =>
      generateChartLabels(
        chartLastYear,
        chartLastMonth,
        chartValues.length,
        locale,
      ),
    [chartLastYear, chartLastMonth, chartValues.length, locale],
  );

  const chartData = useMemo(
    () => chartValues.map((value, i) => ({ name: labels[i], value })),
    [chartValues, labels],
  );

  const scrollableStyle = useMemo(
    () => ({
      width: `${(totalMonths / visibleCount) * 100}%`,
      minWidth: '100%',
    }),
    [totalMonths, visibleCount],
  );

  const chartLabel = t('chartTitle');

  return (
    <section
      id="dashboard"
      className="relative w-screen scroll-mt-[100px] bg-[#f8fafc] py-[60px] md:py-[100px]"
      style={DASHBOARD_BLEED_STYLE}
    >
      <div className="mx-auto flex max-w-[1440px] flex-col items-center gap-[20px] px-5 md:gap-[42px] md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center gap-[18px] text-center md:gap-[22px]"
        >
          {/* Badge */}
          <div className="flex items-center gap-[6px] text-3b-error-primary md:gap-[10px]">
            <ActivityIcon className="h-[13px] w-[13px] md:h-6 md:w-6" />
            <span className="text-[18px] font-semibold leading-[16px] md:text-[24px] md:leading-[29px]">
              {t('badge')}
            </span>
          </div>

          {/* Title */}
          <h2 className="text-[18px] font-bold leading-[1.4] tracking-tight text-[#0f172b] md:text-[48px] md:leading-[1.3]">
            {t.rich('title', {
              highlight: chunks => (
                <span className="text-3b-error-primary">{chunks}</span>
              ),
              br: () => <br />,
              value: thisMonthValue,
            })}
          </h2>

          {/* Subtitle */}
          <p className="text-[14px] font-medium leading-[22px] tracking-[-0.128px] text-[#45556c] md:text-[18px] md:leading-[28px]">
            {t.rich('subtitle', customRichTextTags)}
          </p>
        </motion.div>

        {/* Chart card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-[960px] rounded-[24px] border border-[#f1f5f9] bg-white p-5 shadow-[0px_20px_25px_0px_rgba(226,232,240,0.5),0px_8px_10px_0px_rgba(226,232,240,0.5)] md:p-8"
        >
          {/* Chart header */}
          <div className="mb-5 flex flex-col gap-[4px] md:mb-8 md:flex-row md:items-center md:justify-between">
            <h3 className="text-[16px] font-bold tracking-[-0.34px] text-[#1d293d] md:text-[20px]">
              {chartLabel}
            </h3>
          </div>

          {/* Chart */}
          <div role="img" aria-label={chartLabel}>
            {/* Screen reader accessible data table */}
            <table className="sr-only">
              <caption>{chartLabel}</caption>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map(entry => (
                  <tr key={entry.name}>
                    <td>{entry.name}</td>
                    <td>{entry.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex [&_*]:!outline-none">
              <FixedYAxisChart
                chartData={chartData}
                isDesktop={isDesktop}
                yDomain={yDomain}
              />
              <ScrollableChart
                chartData={chartData}
                isDesktop={isDesktop}
                scrollableStyle={scrollableStyle}
                scrollRef={scrollRef}
                chartLabel={chartLabel}
                onChartClick={handleClick}
                onKeyDown={handleKeyDown}
                yDomain={yDomain}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
